import { useEffect, useMemo, useState } from "react"
import { findNodeAtLocation, parseTree, type JSONPath } from "jsonc-parser"
import type { editor } from "monaco-editor"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Select from "@/v2-ui/select"
import Slider from "@/v2-ui/slider"
import Switch from "@/v2-ui/switch"
import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import {
  applyInspectorEdit,
  describeEntries,
  getInspectorState,
  type InspectorEntry,
} from "@/v2-backoffice-client/layer/update/inspector/layer.update.inspector.utils"
import LayerUpdateInspectorGuided from "@/v2-backoffice-client/layer/update/inspector/LayerUpdateInspectorGuided"
import type { JsonSchema } from "@/v2-backoffice-client/feature/configuration/feature.configuration.types"
import type { LayerEditorField } from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"

export type LayerUpdateInspectorProps = {
  className?: string
  editor: editor.IStandaloneCodeEditor | null
  schema: JsonSchema
  /** Which blob the editor holds; drives the guided widgets. */
  field?: LayerEditorField
  /** The layer's service url; unlocks the metadata-aware guided widgets. */
  serviceUrl?: string | null
  /** Renders an X in the panel header that closes the panel. */
  onClose?(): void
}

/**
 * The inspector lens: a structured panel beside the JSON editor, both live
 * on the same document. It follows the editor cursor — put the cursor inside
 * `renderer` and it shows renderer properties — and renders a widget per
 * property (switches, enum dropdowns, sliders, color pickers). Widget edits
 * are applied as minimal AST-preserving patches through the editor's undo
 * stack; the JSON always stays the source of truth.
 */
function LayerUpdateInspector(props: LayerUpdateInspectorProps) {
  const {
    editor: editorInstance,
    schema,
    field,
    serviceUrl,
    className,
    onClose,
  } = props

  const [snapshot, setSnapshot] = useState<{
    text: string
    offset: number
  } | null>(null)

  useEffect(() => {
    if (!editorInstance) return

    function refresh() {
      const model = editorInstance?.getModel()
      const position = editorInstance?.getPosition()
      if (!model || !position) return
      setSnapshot({
        text: model.getValue(),
        offset: model.getOffsetAt(position),
      })
    }

    refresh()
    const cursorSub = editorInstance.onDidChangeCursorPosition(refresh)
    const contentSub = editorInstance.onDidChangeModelContent(refresh)
    return () => {
      cursorSub.dispose()
      contentSub.dispose()
    }
  }, [editorInstance])

  const state = useMemo(() => {
    if (!snapshot) return null
    return getInspectorState(snapshot.text, snapshot.offset)
  }, [snapshot])

  const entries = useMemo(() => {
    if (!state) return []
    return describeEntries(state, schema)
  }, [state, schema])

  function jumpToPath(path: JSONPath) {
    const model = editorInstance?.getModel()
    if (!model || !editorInstance) return
    const root = parseTree(model.getValue())
    if (!root) return
    const node = path.length === 0 ? root : findNodeAtLocation(root, path)
    if (!node) return
    // +1 lands inside the value (e.g. within the braces of an object).
    const position = model.getPositionAt(node.offset + 1)
    editorInstance.setPosition(position)
    editorInstance.revealPositionInCenterIfOutsideViewport(position)
    editorInstance.focus()
  }

  if (!editorInstance) return null

  return (
    <div
      className={cn(
        "layer-update-inspector flex flex-col gap-1 overflow-y-auto rounded-md border p-3 text-xs",
        className,
      )}
    >
      {onClose && (
        <header className="mb-1 flex shrink-0 items-center justify-between text-xs font-medium">
          Inspector
          <Button
            variant="ghost"
            size="xs"
            iconSrc="cross-small"
            className="-mr-1.5 -mt-1"
            aria-label="Close the inspector"
            data-feature-code="UI_BackofficeLayers_Inspector_Close"
            onClick={onClose}
          />
        </header>
      )}
      {field && snapshot && (
        <LayerUpdateInspectorGuided
          editor={editorInstance}
          field={field}
          text={snapshot.text}
          serviceUrl={serviceUrl ?? null}
        />
      )}

      <p className="mb-1 flex flex-wrap items-center gap-0.5 font-mono text-[11px] text-muted-foreground">
        <button
          type="button"
          className="hover:text-foreground"
          onClick={() => jumpToPath([])}
        >
          root
        </button>
        {state?.path.map((segment, index) => (
          <span key={index} className="flex items-center gap-0.5">
            <Icon src="angle-right" size={10} />
            <button
              type="button"
              className="hover:text-foreground"
              onClick={() => jumpToPath(state.path.slice(0, index + 1))}
            >
              {String(segment)}
            </button>
          </span>
        ))}
      </p>

      {!state && (
        <p className="text-muted-foreground">
          Fix the JSON syntax to use the inspector.
        </p>
      )}

      {state && entries.length === 0 && (
        <p className="text-muted-foreground">
          Empty object — add properties in the editor (autocomplete knows the
          valid keys).
        </p>
      )}

      {entries.map((entry) => (
        <InspectorRow
          key={`${entry.path.join(".")}:${String(entry.value)}`}
          entry={entry}
          onEdit={(value) => {
            applyInspectorEdit(editorInstance, entry.path, value)
          }}
          onJump={() => jumpToPath(entry.path)}
        />
      ))}
    </div>
  )
}

type InspectorRowProps = {
  entry: InspectorEntry
  onEdit(value: unknown): void
  onJump(): void
}

function InspectorRow(props: InspectorRowProps) {
  const { entry, onEdit, onJump } = props

  return (
    <div
      className="layer-update-inspector-row grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-center gap-2 py-0.5"
      title={entry.description}
    >
      <span className="truncate font-mono text-[11px]" title={entry.key}>
        {entry.key}
      </span>
      <InspectorWidget entry={entry} onEdit={onEdit} onJump={onJump} />
    </div>
  )
}

function InspectorWidget(props: InspectorRowProps) {
  const { entry, onEdit, onJump } = props

  if (entry.kind === "boolean") {
    return (
      <Switch
        checked={Boolean(entry.value)}
        onCheckedChange={(checked) => onEdit(checked)}
      />
    )
  }

  if (entry.kind === "enum" && entry.enumValues) {
    return (
      <Select
        value={String(entry.value)}
        onValueChange={(next) => onEdit(next)}
      >
        <Select.Trigger size="xs" className="h-6 w-full text-[11px]">
          <Select.Value />
        </Select.Trigger>
        <Select.Content>
          {entry.enumValues.map((option) => (
            <Select.Item key={option} value={option}>
              {option}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
    )
  }

  if (
    entry.kind === "number" &&
    entry.minimum !== undefined &&
    entry.maximum !== undefined
  ) {
    return (
      <span className="flex items-center gap-2">
        <Slider
          value={[Number(entry.value)]}
          min={entry.minimum}
          max={entry.maximum}
          step={(entry.maximum - entry.minimum) / 20}
          onValueChange={([next]) => onEdit(next)}
        />
        <span className="w-8 text-right font-mono text-[11px]">
          {Number(entry.value)}
        </span>
      </span>
    )
  }

  if (entry.kind === "number") {
    return (
      <Input
        type="number"
        className="h-6 text-[11px]"
        defaultValue={String(entry.value)}
        onBlur={(event) => {
          const next = Number(event.target.value)
          if (!Number.isNaN(next) && next !== entry.value) onEdit(next)
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur()
        }}
      />
    )
  }

  if (entry.kind === "color") {
    return (
      <span className="flex items-center gap-2">
        <input
          type="color"
          className="h-6 w-8 cursor-pointer rounded border bg-transparent"
          value={String(entry.value)}
          onChange={(event) => onEdit(event.target.value)}
        />
        <span className="font-mono text-[11px]">{String(entry.value)}</span>
      </span>
    )
  }

  if (entry.kind === "object" || entry.kind === "array") {
    return (
      <button
        type="button"
        className="flex items-center gap-1 justify-self-start text-muted-foreground hover:text-foreground"
        onClick={onJump}
      >
        {entry.kind === "object" ? "{…}" : "[…]"}
        <Icon src="arrow-right" size={10} />
      </button>
    )
  }

  if (entry.kind === "null") {
    return (
      <span className="font-mono text-[11px] text-muted-foreground">null</span>
    )
  }

  return (
    <Input
      className="h-6 text-[11px]"
      defaultValue={String(entry.value ?? "")}
      onBlur={(event) => {
        if (event.target.value !== entry.value) onEdit(event.target.value)
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur()
      }}
    />
  )
}

export default LayerUpdateInspector
